import React, { useEffect, useState } from 'react'
import JajotApi from '../../api/JajotApi';
import { Button, Row, Col, Form, Container } from 'react-bootstrap'
import { useForm, Controller } from 'react-hook-form';
import { FaArrowLeft } from 'react-icons/fa'
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from "react-select";
import toast from 'react-hot-toast';
import { Shimmer } from "react-shimmer";
import jwtDecode from 'jwt-decode';

const TaskWizard = ({ show, hideHeader = true, relatedToId = null, taskToEdit = null, selectedDate = null, onTaskCreated = null }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [taskDetail, setTasksDetail] = useState(null);
    const [employeeData, setEmployeeData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isResponseHas, setIsResponseHas] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        try {
            const token = localStorage.getItem('token');
            if (token) {
                const decoded = jwtDecode(token);
                setCurrentUserId(decoded?.contactDetails?.Id || null);
            }
        } catch (err) {
            console.error('Failed to decode token:', err);
        }
    }, []);

    const formResetData = (data = {}) => {
        // Read Salesforce fields correctly
        const startSF = data?.Start_Date__c || data?.StartDate;
        const endSF = data?.End_Date__c || data?.EndDate;

        // Handle selectedDate - it could be just date (YYYY-MM-DD) or datetime (YYYY-MM-DDTHH:mm)
        let defaultStartDate = moment().format("YYYY-MM-DDTHH:mm");
        if (selectedDate) {
            // Check if selectedDate already includes time (has 'T' in it)
            if (selectedDate.includes('T')) {
                // Already has time, use it as is (may need to format it properly)
                defaultStartDate = moment(selectedDate).format("YYYY-MM-DDTHH:mm");
            } else {
                // Only date, append default time
                defaultStartDate = `${selectedDate}T09:00`;
            }
        }

        const startLocal = startSF
            ? moment(startSF).local().format("YYYY-MM-DDTHH:mm")
            : defaultStartDate;

        const endLocal = endSF
            ? moment(endSF).local().format("YYYY-MM-DDTHH:mm")
            : moment(startLocal).add(1, 'hour').format("YYYY-MM-DDTHH:mm");

        return {
            Title: data?.Title__c || data?.Title || null,
            Status: data?.Status__c || data?.Status || "New",
            OwnerId: data?.OwnerId__c || data?.OwnerId || currentUserId || null,
            StartDate: startLocal,
            EndDate: endLocal,
            Priority: data?.Priority__c || data?.Priority || "High",
            Description: data?.Description__c || data?.Description || null,

            Assignees:
                data?.Assignees?.map((element) => ({
                    recordId: element?.Id || element?.recordId,
                    value: element?.ContactId__c || element?.assigneeId,
                    label:
                        employeeData?.find(
                            (u) => u?.Id === (element?.ContactId__c || element?.assigneeId)
                        )?.Name || ''
                })) || []
        };
    };


    const { register, handleSubmit, formState: { errors }, reset, watch, control } = useForm();
    const startDate = watch("StartDate");
    // First fetch Employee Data
    useEffect(() => {
        fetchEmployeeData();
    }, [show, location.state]);

    const lastTaskIdRef = React.useRef(null);

    useEffect(() => {
        if (!employeeData) return;
        const existing = taskToEdit || location.state?.existingTask;
        const currentId = existing?.Id || null;

        // Avoid repeated resets when toggling modal/show without task change
        if (lastTaskIdRef.current === currentId && taskDetail?.Id === currentId) return;

        setTasksDetail(existing);
        const initialData = existing ? formResetData(existing) : formResetData({});
        reset(initialData);
        lastTaskIdRef.current = currentId;
        console.log('Initial Form Data:', initialData, "Existing Task:", existing);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeData, taskToEdit, location.state]);

    const fetchEmployeeData = async () => {
        try {
            setIsLoading(true);
            const response = await JajotApi.getEmployeeRecords();

            if (response.success) {
                const employeeOnly = response.records.filter(record => record.Type?.includes('Employee'));

                console.log('Filtered Employees:', employeeOnly);

                setEmployeeData(employeeOnly);
            } else {
                setEmployeeData([]);
                toast.error("No employee records found");
            }
        } catch (error) {
            setEmployeeData([]);
            console.error('Error fetching employees:', error);
            toast.error("Failed to fetch employees");
        } finally {
            setIsLoading(false);
        }
    };


    const onSubmit = async (formData) => {
        try {
            setIsResponseHas(true);
            const taskType = onTaskCreated ? 'Event' : 'Task';
            formData = {
                ...formData,
                type: taskType,
                StartDate: formData.StartDate
                    ? moment(formData.StartDate).utc().format()
                    : null,

                EndDate: formData.EndDate
                    ? moment(formData.EndDate).utc().format()
                    : null,

                Assignees: formData?.Assignees?.map(item => ({
                    assigneeId: item?.value,
                    Id: item?.recordId
                }))
            };
            let response;
            if (taskDetail?.Id) {
                const previousUserObjects = taskDetail?.Assignees || [];
                const currentUserIds = formData.Assignees?.map(u => u.Id);

                formData.deleteAssigneeRecordIds = previousUserObjects?.filter(p => !currentUserIds.includes(p.recordId))?.map(p => p?.recordId);

                response = await JajotApi.updateTaskById(formData, taskDetail?.Id);
                if (response.success) {
                    toast.success(response.message);
                    if (onTaskCreated) {
                        reset(formResetData({}));
                        setTasksDetail(null);
                        onTaskCreated();
                    } else if (!relatedToId) {
                        navigate("/task");
                    }
                } else {
                    toast.error("Failed to update task");
                }
            } else {
                response = await JajotApi.addNewTask(formData, relatedToId);
                if (response.success) {
                    toast.success(response.message);
                    if (onTaskCreated) {
                        reset(formResetData({}));
                        setTasksDetail(null);
                        onTaskCreated();
                    } else if (!relatedToId) {
                        navigate("/task");
                    }
                } else {
                    toast.error("Failed to create task");
                }
            }
        } catch (error) {
            console.log('Error create task:', error);
            toast.error("Inertnal server error");
        } finally {
            setIsResponseHas(false);
        }
    }

    return (
        <Container fluid className="py-4">
            {isLoading ? (
                <div className="p-3">
                    <Shimmer width={600} height={30} className="mb-3" />
                    <Shimmer width={-1} height={20} className="mb-2" />
                    <Shimmer width={-1} height={20} className="mb-2" />
                </div>
            ) : (
                <div className="bg-white shadow-sm rounded p-3">
                    {hideHeader && <div className="mb-4 d-flex align-items-center gap-3">
                        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                            <FaArrowLeft className="me-2" /> Back To Task
                        </Button>
                        <h3 className="text-primary mb-0">
                            {location.state?.existingTask?.Id ? 'Edit' : 'Create'} Task
                        </h3>
                    </div>}
                    <h6 className="fw-bold border-bottom pb-2 mb-3">{taskDetail?.Id ? 'Edit' : 'Fill in'} Task Details</h6>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                                    {errors.Title && (
                                        <span className="text-danger ms-2" style={{ whiteSpace: "nowrap" }}>
                                            {errors.Title.message}
                                        </span>)}
                                    <Form.Control
                                        type='text'
                                        size="sm"
                                        defaultValue={taskDetail?.Title || ""}
                                        placeholder="Enter task title"
                                        {...register("Title", {
                                            required: "Required",
                                            validate: (value) => {
                                                if (value === "") return true;
                                                if (value?.trim() === "")
                                                    return "Please enter a valid input";
                                                return (/^[A-Za-z0-9\s().,!'’-]+$/.test(value) || "Please enter a valid input");
                                            },
                                        })}
                                        isInvalid={!!errors.Title}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        defaultValue={taskDetail?.Status || ""}
                                        size="sm"
                                        {...register("Status")}
                                    >
                                        <option value="" disabled>Select status</option>
                                        <option value="New">New</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Approval">Approval</option>
                                        <option value="Completed">Completed</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date and Time <span className="text-danger">*</span></Form.Label>
                                    {errors.StartDate && (<span className="text-danger ms-2">{errors.StartDate.message}</span>)}
                                    <Form.Control
                                        type="datetime-local"
                                        size="sm"
                                        placeholder="Select date and time"
                                        {...register("StartDate", {
                                            required: "Required"
                                        })}
                                        isInvalid={!!errors.StartDate}
                                    />
                                    <Form.Text className="text-muted">Select both date and time</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date and Time <span className="text-danger">*</span></Form.Label>
                                    {errors.EndDate && (<span className="text-danger ms-2">{errors.EndDate.message}</span>)}
                                    <Form.Control
                                        type="datetime-local"
                                        size="sm"
                                        placeholder="Select date and time"
                                        {...register("EndDate", {
                                            required: "Required", validate: (value) =>
                                                !value || !startDate || value >= startDate
                                                    ? true
                                                    : "End Date and Time cannot be earlier than Start Date and Time",
                                        })}
                                        isInvalid={!!errors.EndDate}
                                    />
                                    <Form.Text className="text-muted">Select both date and time</Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Priority</Form.Label>
                                    <Form.Select
                                        defaultValue={taskDetail?.Priority || "High"}
                                        size="sm"
                                        {...register("Priority")}
                                    >
                                        <option value="" disabled>Select Priority</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Owner</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        defaultValue={taskDetail?.OwnerId}
                                        {...register("OwnerId")}>
                                        <option value="" disabled>Select Owner</option>
                                        {employeeData?.map((element) => (
                                            <option key={element?.Id} value={element?.Id}>
                                                {element?.Name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Assigned To <span className="text-danger">*</span></Form.Label>
                                    {errors.Assignees && <span className="text-danger ms-2" style={{ whiteSpace: "nowrap" }}>{errors.Assignees.message}</span>}
                                    <Controller
                                        name="Assignees"
                                        control={control}
                                        defaultValue={
                                            taskDetail?.Assignees?.map((element) => ({
                                                value: element.assigneeId,
                                                label: employeeData?.find((u) => u?.Id === element.assigneeId)?.Name || element.assigneeId,
                                            })) || []
                                        }
                                        rules={{ required: "Required" }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                size="sm"
                                                isMulti
                                                options={employeeData?.map((item) => ({ value: item?.Id, label: item?.Name })) || []}
                                                classNamePrefix="react-select"
                                                placeholder="Select Notify Members..."
                                                onChange={(selectedOptions) => {
                                                    field.onChange(selectedOptions);
                                                }}
                                            />
                                        )}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea" rows={1}
                                        defaultValue={taskDetail?.Description || ""}
                                        placeholder="Task description..."
                                        {...register("Description")}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-`">
                            <Button disabled={isResponseHas} className='btn-sm' type='submit' variant="success">{taskDetail?.Id ? 'Update' : 'Save'} Task</Button>
                        </div>
                    </Form>
                </div>
            )}
        </Container>
    )
};

export default TaskWizard